/**
 * Adapter that maps /api/expert/preview response → the legacy state
 * shapes SummaryPage already consumes.
 *
 * The preview response is Gen-3-shaped:
 *   { profiler_brief, cities: [{ name, days, available_hours,
 *     preview_lines: [3 strings], anchor_places: [...] }] }
 *
 * The existing SummaryPage consumes:
 *   - profilerBrief                      (App.js state — handoff to /itinerary/generate-from-pipeline)
 *   - curatorBrief.cities[name].available_hours  (only field still read post-A.2 — getCityHours)
 *   - extractedPlaces[]                  (place cards rendered as Netflix-style rows)
 *   - selectedPlaces[]                   (which names are currently selected)
 *   - aiAnalyses[city].actions           (the 3-bullet "trip at a glance" lines)
 *
 * Keeping the legacy state shapes means A.2 is purely a data-source swap;
 * no UI component changes. A.3 deletes the now-unused curatorBrief shape.
 */

export function adaptPreviewToLegacy(previewResponse) {
  const profilerBrief = previewResponse?.profiler_brief ? {
    ...previewResponse.profiler_brief,
    _request_context: previewResponse.request_context || null,
    _preview_token: previewResponse.preview_token || null,
    _request_decisions: [],
  } : null;
  const cities = previewResponse?.cities || [];

  // Minimal curatorBrief — only the field getCityHours() reads.
  // Once A.3 lands and the curator is deleted, this shrinks to just the
  // hours map and we can replace getCityHours() with a direct lookup.
  const curatorBrief = {
    cities: Object.fromEntries(
      cities.map((c) => [c.name, { available_hours: c.available_hours || 0 }])
    ),
  };

  // Anchor places, flattened into the legacy place card shape.
  // We ALSO keep `previewPicks` — the raw anchor_places per city — so the
  // SummaryPage can round-trip them to /itinerary/generate-from-pipeline.
  // Reusing the preview's picks means we don't run the Selector AI call
  // twice (preview + full trip), which had been producing different picks
  // on the second run.
  const places = [];
  const allNames = [];
  const previewPicks = [];
  for (const city of cities) {
    for (const a of (city.anchor_places || [])) {
      const cardName = a.name || "";
      if (!cardName) continue;
      places.push({
        name: cardName,
        city: city.name,
        neighborhood: a.neighborhood || "",
        category: (a.category || "").toLowerCase(),
        description: a.why || "",
        why: a.why || "",
        image: a.image || "",
        // a free photo's licence asks for its credit wherever it is shown (2026-10-07: dropped here)
        image_source: a.image_source || "",
        image_credit: a.image_credit || "",
        place_id: a.place_id || "",
        address: a.address || "",
        is_hidden_gem: !!a.is_hidden_gem,
        // "your pick": a place that answers something they asked for, or one they added by hand
        // (it was always false, so the "Your picks" filter emptied the page and hid itself)
        from_wishlist: !!(a.is_sacred || a.user_added),
        from_catalog: a.from_catalog !== false,
        // Real duration from the pick (was hardcoded 1.5 — every Summary
        // card showed the same time regardless of the place).
        duration: typeof a.duration_h === "number" ? a.duration_h : 1.5,
        cost_estimate: 0,
        // good at dawn and after dark: which one the trip plans (the selection page offers the other)
        moments: a.moments || null,
      });
      allNames.push(cardName);
      // Raw shape the backend's pick-hydration consumes. Carries the FULL
      // v8 round-trip fields — meal_role (so meals anchor correctly),
      // description/tip (so off-catalog special cards survive the
      // preview→generate hop with their data intact), is_sacred.
      previewPicks.push({
        ...a,
        name: cardName,
        city: city.name,
        neighborhood: a.neighborhood || "",
        category: (a.category || "").toUpperCase(),
        why: a.why || "",
        duration_h: typeof a.duration_h === "number" ? a.duration_h : 1.5,
        from_catalog: a.from_catalog !== false,
        is_sacred: !!a.is_sacred,
        meal_role: a.meal_role || null,
        description: a.description || "",
        tip: a.tip || "",
      });
    }
  }

  // Per-city analyses — preview_lines are the "trip at a glance"
  // bullets. recommendations = alternative_places (the Selector now emits
  // 4-6 per city, mapped into the legacy "You might also love" surface).
  const analyses = {};
  for (const city of cities) {
    const recs = (city.alternative_places || []).map((a) => ({
      name: a.name || "",
      city: city.name,
      neighborhood: a.neighborhood || "",
      category: (a.category || "").toLowerCase(),
      description: a.why || "",
      why: a.why || "",
      image: a.image || "",
      address: a.address || "",
      is_hidden_gem: !!a.is_hidden_gem,
      from_catalog: a.from_catalog !== false,
      duration: typeof a.duration_h === "number" ? a.duration_h : 1.5,
      cost_estimate: 0,
    }));
    analyses[city.name] = {
      actions: (city.preview_lines || []).slice(0, 3),
      recommendations: recs,
    };
  }

  // dayBrief — code-only structured per-day shape produced by the backend.
  // SummaryPage stashes it; the generate call sends it back so both AI
  // stages see the same frozen skeleton.
  const dayBrief = previewResponse?.day_brief || null;

  return { profilerBrief, curatorBrief, places, allNames, analyses, previewPicks, dayBrief };
}
